/*
  Predictive search rebuild.

  The existing behaviour (bundled inside theme.s.min.js, which is
  intentionally left untouched — see ORDERING-PROTECTED-FILES.md) binds a
  plain "keyup change" handler directly on every `form[action="/search"]`
  input with no debounce, no keyboard navigation, and renders into a
  ".search-results" div this stylesheet now hides. Rather than edit that
  bundled file, this script:
    1. Intercepts keyup/change on the same inputs during the CAPTURE phase
       at the document level (which always runs before a target's own
       bubble-phase listeners, regardless of script load order) and calls
       stopImmediatePropagation() so the legacy handler never fires.
    2. Implements the same debounced fetch against the theme's existing
       /search?type=product&q=...&view=json endpoint (now returning price/
       availability too, see templates/search.json.liquid), rendered into a
       new, namespaced, keyboard-navigable, ARIA combobox-pattern dropdown.

  No new markup is required in the header — it attaches to the existing
  form(s) already in sections/header-template-2.liquid.
*/
(function () {
  "use strict";

  var SELECTOR = 'form[action="/search"] input[name="q"]';
  var MIN_LENGTH = 2;
  var DEBOUNCE_MS = 300;

  var managed = new Map(); // input element -> { ... state }
  var idCounter = 0;

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
      return (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[
          c
        ] || c
      );
    });
  }

  function closeDropdown(state) {
    state.dropdown.hidden = true;
    state.input.setAttribute("aria-expanded", "false");
    state.input.removeAttribute("aria-activedescendant");
    state.activeIndex = -1;
  }

  function openDropdown(state) {
    state.dropdown.hidden = false;
    state.input.setAttribute("aria-expanded", "true");
  }

  function setActive(state, index) {
    var options = state.dropdown.querySelectorAll('[role="option"]');
    if (!options.length) return;
    options.forEach(function (opt) {
      opt.setAttribute("aria-selected", "false");
    });
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    state.activeIndex = index;
    var active = options[index];
    active.setAttribute("aria-selected", "true");
    state.input.setAttribute("aria-activedescendant", active.id);
    active.scrollIntoView({ block: "nearest" });
  }

  function renderResults(state, data, term) {
    var results = (data && data.results) || [];
    var count = (data && data.results_count) || 0;
    var html = "";

    if (!results.length) {
      html =
        '<p class="tt-predictive-search__empty">No results for &ldquo;' +
        escapeHtml(term) +
        "&rdquo;</p>";
    } else {
      results.forEach(function (item, i) {
        var optId = state.idBase + "-opt-" + i;
        var priceHtml = "";
        if (item.available === false) {
          priceHtml =
            '<span class="tt-predictive-search__unavailable">Out of stock</span>';
        } else if (item.compare_at_price) {
          priceHtml =
            '<span class="tt-predictive-search__price tt-predictive-search__price--sale">' +
            escapeHtml(item.price) +
            '</span> <span class="tt-predictive-search__compare">' +
            escapeHtml(item.compare_at_price) +
            "</span>";
        } else if (item.price) {
          priceHtml =
            '<span class="tt-predictive-search__price">' +
            escapeHtml(item.price) +
            "</span>";
        }
        html +=
          '<a id="' +
          optId +
          '" role="option" aria-selected="false" class="tt-predictive-search__item" href="' +
          encodeURI(item.url) +
          '">' +
          (item.thumbnail
            ? '<img class="tt-predictive-search__thumb" src="' +
              encodeURI(item.thumbnail) +
              '" alt="" loading="lazy" decoding="async">'
            : "") +
          '<span class="tt-predictive-search__body">' +
          '<span class="tt-predictive-search__title">' +
          escapeHtml(item.title) +
          "</span>" +
          '<span class="tt-predictive-search__meta">' +
          priceHtml +
          "</span>" +
          "</span>" +
          "</a>";
      });

      html +=
        '<a role="option" aria-selected="false" class="tt-predictive-search__viewall" href="/search?type=product&q=' +
        encodeURIComponent(term) +
        '">View all ' +
        count +
        ' result' +
        (count === 1 ? "" : "s") +
        "</a>";
    }

    state.dropdown.innerHTML = html;
    state.activeIndex = -1;
    openDropdown(state);

    state.dropdown
      .querySelectorAll('[role="option"]')
      .forEach(function (opt, i) {
        opt.addEventListener("mouseenter", function () {
          setActive(state, i);
        });
      });
  }

  function runSearch(state) {
    var term = state.input.value.trim();
    if (term.length < MIN_LENGTH) {
      closeDropdown(state);
      return;
    }
    if (term === state.lastTerm) return;
    state.lastTerm = term;

    if (state.controller) state.controller.abort();
    state.controller = "AbortController" in window ? new AbortController() : null;

    fetch(
      "/search?type=product&q=" + encodeURIComponent(term) + "&view=json",
      state.controller ? { signal: state.controller.signal } : {}
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        renderResults(state, data, term);
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
      });
  }

  function initInput(input) {
    if (managed.has(input)) return;

    var wrapper = input.closest("form") || input.parentElement;
    if (getComputedStyle(wrapper).position === "static") {
      wrapper.style.position = "relative";
    }

    idCounter += 1;
    var idBase = "tt-predictive-search-" + idCounter;

    var dropdown = document.createElement("div");
    dropdown.className = "tt-predictive-search";
    dropdown.id = idBase;
    dropdown.setAttribute("role", "listbox");
    dropdown.hidden = true;
    wrapper.appendChild(dropdown);

    input.setAttribute("role", "combobox");
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-controls", idBase);
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("autocomplete", "off");

    var state = {
      input: input,
      dropdown: dropdown,
      idBase: idBase,
      activeIndex: -1,
      lastTerm: null,
      controller: null,
    };

    managed.set(input, {
      state: state,
      debounced: debounce(function () {
        runSearch(state);
      }, DEBOUNCE_MS),
    });

    document.addEventListener("click", function (e) {
      if (!wrapper.contains(e.target)) closeDropdown(state);
    });
  }

  function handleKeyup(e) {
    var entry = managed.get(e.target);
    if (!entry) return;
    if (
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "Enter" ||
      e.key === "Escape" ||
      e.key === "Tab"
    ) {
      return;
    }
    entry.debounced();
  }

  function handleKeydown(e) {
    var entry = managed.get(e.target);
    if (!entry) return;
    var state = entry.state;
    if (state.dropdown.hidden && e.key !== "Escape") return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(state, state.activeIndex + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(state, state.activeIndex - 1);
    } else if (e.key === "Escape") {
      closeDropdown(state);
    } else if (e.key === "Enter") {
      if (state.activeIndex >= 0) {
        var options = state.dropdown.querySelectorAll('[role="option"]');
        var active = options[state.activeIndex];
        if (active && active.href) {
          e.preventDefault();
          window.location.href = active.href;
        }
      }
      // else: let the form submit normally to the full /search results page
    }
  }

  document.addEventListener(
    "keyup",
    function (e) {
      if (!e.target.matches || !e.target.matches(SELECTOR)) return;
      e.stopImmediatePropagation();
      handleKeyup(e);
    },
    true
  );

  document.addEventListener(
    "change",
    function (e) {
      if (!e.target.matches || !e.target.matches(SELECTOR)) return;
      e.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    "keydown",
    function (e) {
      if (!e.target.matches || !e.target.matches(SELECTOR)) return;
      handleKeydown(e);
    },
    true
  );

  // Both the desktop inline search and the mobile dropdown search form are
  // already present in the initial DOM (the mobile one is just CSS-hidden
  // until its toggle is opened), so a one-time scan on load is sufficient —
  // no MutationObserver needed to catch late-inserted forms.
  document.querySelectorAll(SELECTOR).forEach(initInput);
})();
